import type { ReactNode } from 'react'
import type { Effect } from 'effect'
import type { Rect } from '@st/geom'
import { AnnotationPlugin } from '../../annotation-plugin'
import { type DocumentViewerSend, DocumentViewerPluginDefinition } from '../../document-viewer'
import { ResolvedPageStructure } from '../../models'
import { AnnotationLayerOverlay } from './annotation-overlay'
import {
  ANNOTATIONS_PLUGIN_ID,
  AnnotationsPluginCommand,
  makeAnnotationsPluginStateModule,
  toViewerMessageFromAnnotationToolMessage
} from './annotations-plugin-module'
import {
  AnnotationBase,
  AnnotationMetadata,
  AnnotationLocalMessage,
  AnnotationPluginState,
  AnnotationsPluginInput,
  ToolStateBase
} from './types'

export type AnnotationLayerInput = AnnotationsPluginInput

type AnnotationPlacement = {
  id?: string
  bounds: Rect
}

export type AnnotationsPluginOverlayProps = {
  send: DocumentViewerSend
  page: ResolvedPageStructure
  annotations: AnnotationBase[]
  placements?: readonly AnnotationPlacement[]
  annotationState: AnnotationPluginState
  activeAnnotation: AnnotationBase | undefined
  renderToolOverlay: boolean
}

export type AnnotationsPluginDefinition<
  PluginsInput extends {
    annotations?: AnnotationLayerInput
  }
> = DocumentViewerPluginDefinition<
  PluginsInput,
  'annotations',
  AnnotationLocalMessage,
  AnnotationPluginState,
  AnnotationsPluginCommand
> & {
  Overlay: (props: AnnotationsPluginOverlayProps) => ReactNode
}

export function createAnnotationsPlugin<
  PluginsInput extends {
    annotations?: AnnotationLayerInput
  }
>({
  tools,
  initialTool,
  settingsKey,
  storageNamespace,
  createAnnotationMetadata,
  copyText
}: {
  tools: AnnotationPlugin<AnnotationBase, ToolStateBase>[]
  initialTool: string
  settingsKey: string
  storageNamespace: string
  createAnnotationMetadata: Effect.Effect<AnnotationMetadata, never, any>
  copyText: (text: string) => Effect.Effect<null, never, any>
}): AnnotationsPluginDefinition<PluginsInput> {
  const Overlay = ({
    send,
    page,
    annotations,
    placements = [],
    annotationState,
    activeAnnotation,
    renderToolOverlay
  }: AnnotationsPluginOverlayProps) => {
    if (page.isDeleted == true) {
      return null
    }

    const toolState = annotationState.toolStates[annotationState.selectedToolName]

    return (
      <AnnotationLayerOverlay
        model={{
          annotations: placeAnnotations(annotations, placements),
          toolState,
          activeAnnotation,
          selectionState: annotationState.selectionState,
          tools
        }}
        page={page}
        renderToolOverlay={renderToolOverlay}
        send={(message) => send(toViewerMessageFromAnnotationToolMessage(message))}
      />
    )
  }

  return {
    id: ANNOTATIONS_PLUGIN_ID,
    createStateModule: () =>
      makeAnnotationsPluginStateModule({
        tools,
        initialTool,
        settingsKey,
        storageNamespace,
        createAnnotationMetadata,
        copyText
      }),
    Overlay
  }
}

function placeAnnotations(
  annotations: AnnotationBase[],
  placements: readonly AnnotationPlacement[]
): AnnotationBase[] {
  // Renderers may leave an annotation semantic until layout determines its
  // bounds. Resolve that geometry at the DVK presentation boundary so tools
  // and callers only deal with one annotation model.
  return annotations.flatMap((annotation) => {
    if (annotation.bounds) return [annotation]

    const placement = placements.find((candidate) => candidate.id == annotation.id)
    return placement ? [{ ...annotation, bounds: placement.bounds }] : []
  })
}
